// RigSentinel Context Engine — Phase RCE-1
//
// CONTEXT INSPECTOR (developer tool · /dev/context-inspector)
// ----------------------------------------------------------
// Read-only debug view of how the RCE resolves data against the current
// Engineering Session + any field path. Not surfaced in the normal user
// navigation — accessible via the Active Operation Pill's "Inspect" button
// or by typing the URL directly. Designed for engineering debugging as the
// app grows.

import React, { useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ChevronLeft, Search } from "lucide-react";
import { useEngineeringSession } from "@/lib/rce/useEngineeringSession";
import { FIELD_REGISTRY, classifyField } from "@/lib/rce/classification";
import { resolve } from "@/lib/rce/resolver";
import { listOverrides } from "@/lib/rce/overrides";
import { summariseHealth } from "@/lib/rce/health";

function _color(confidence) {
  return {
    exact:     "#22C55E",
    override:  "#A855F7",
    inherited: "#FFB300",
    derived:   "#60A5FA",
    conflict:  "#EF4444",
    unknown:   "#6B7280",
  }[confidence] || "#6B7280";
}

export default function ContextInspector() {
  const navigate = useNavigate();
  const { session } = useEngineeringSession();
  const [filter, setFilter] = useState("");
  const [docId, setDocId]   = useState("");

  const ctx = useMemo(() => ({
    documentId:    docId || undefined,
    projectId:     session?.projectId,
    rigId:         session?.rigId,
    wellId:        session?.wellId,
    bhaId:         session?.bhaId,
    drillStringId: session?.drillStringId,
  }), [session, docId]);

  const fields = useMemo(() => {
    const all = Object.keys(FIELD_REGISTRY);
    if (!filter.trim()) return all;
    const f = filter.toLowerCase();
    return all.filter((k) => k.toLowerCase().includes(f));
  }, [filter]);

  const overrides = useMemo(() => {
    return [
      ...listOverrides({ scopeId: docId }),
      ...(session?.wellId ? listOverrides({ scopeId: session.wellId }) : []),
      ...(session?.rigId ? listOverrides({ scopeId: session.rigId }) : []),
      ...(session?.projectId ? listOverrides({ scopeId: session.projectId }) : []),
    ];
  }, [docId, session]);

  return (
    <div className="pb-8" data-testid="context-inspector">
      <div className="flex items-center gap-3 mb-4">
        <button
          data-testid="ctx-inspector-back"
          onClick={() => navigate(-1)}
          className="btn-ghost h-9 w-9 rounded-md inline-flex items-center justify-center"
          aria-label="Back"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="flex-1">
          <h1 className="font-heading font-black text-xl">Context Inspector</h1>
          <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--rs-text-3)]">
            Developer tool · RCE Phase 1
          </div>
        </div>
        <Link
          to="/dev/inheritance-trace"
          data-testid="ctx-inspector-trace-link"
          className="text-[11px] text-amber-400 hover:underline"
        >
          Inheritance Trace →
        </Link>
      </div>

      <div
        className="rounded-md border p-3 mb-3 text-[11px] text-[var(--rs-text-2)] space-y-1"
        style={{ borderColor: "var(--rs-border)", background: "rgba(22,24,29,0.65)" }}
        data-testid="ctx-inspector-session"
      >
        <div className="label-up text-[9.5px] text-[var(--rs-text-3)]">Active Engineering Session</div>
        {session ? (
          <ul className="grid grid-cols-2 gap-x-3 gap-y-0.5">
            <li><b>Project:</b> {session.projectId || "—"}</li>
            <li><b>Rig:</b> {session.rigId || "—"}</li>
            <li><b>Well:</b> {session.wellId || "—"}</li>
            <li><b>BHA:</b> {session.bhaId || "—"}</li>
            <li><b>Drill string:</b> {session.drillStringId || "—"}</li>
            <li><b>Hole section:</b> {session.holeSection || "—"}</li>
            <li className="col-span-2"><b>Operation:</b> {session.currentOperation || "—"}</li>
          </ul>
        ) : (
          <div>No active session. Use the Active Operation pill to start one.</div>
        )}
      </div>

      {/* Context Health — RCE-1 stub. Full dashboard reserved for a later phase. */}
      <HealthStrip ctx={ctx} />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
        <label className="block">
          <div className="label-up text-[9.5px] text-[var(--rs-text-3)] mb-1">Filter fields</div>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[var(--rs-text-3)]" />
            <input
              data-testid="ctx-inspector-filter"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="e.g. well.current"
              className="w-full h-9 pl-7 pr-2 rounded border bg-[rgba(10,11,14,0.75)] text-[12px] text-[var(--rs-text-1)]"
              style={{ borderColor: "var(--rs-border)" }}
            />
          </div>
        </label>
        <label className="block">
          <div className="label-up text-[9.5px] text-[var(--rs-text-3)] mb-1">documentId (optional)</div>
          <input
            data-testid="ctx-inspector-docid"
            value={docId}
            onChange={(e) => setDocId(e.target.value)}
            placeholder="kill sheet id, report id, …"
            className="w-full h-9 px-2 rounded border bg-[rgba(10,11,14,0.75)] text-[12px] text-[var(--rs-text-1)]"
            style={{ borderColor: "var(--rs-border)" }}
          />
        </label>
      </div>

      <div
        className="rounded-md border overflow-hidden"
        style={{ borderColor: "var(--rs-border)", background: "rgba(22,24,29,0.65)" }}
      >
        <div className="grid grid-cols-[1.6fr_1fr_0.7fr_1.4fr] gap-2 px-3 py-2 text-[10px] label-up text-[var(--rs-text-3)] border-b" style={{ borderColor: "var(--rs-border)" }}>
          <div>Field</div>
          <div>Value</div>
          <div>Confidence</div>
          <div>Source</div>
        </div>
        <ul data-testid="ctx-inspector-rows">
          {fields.map((f) => {
            const r = resolve(f, ctx);
            return (
              <li key={f} data-testid={`ctx-row-${f}`} className="grid grid-cols-[1.6fr_1fr_0.7fr_1.4fr] gap-2 px-3 py-1.5 text-[11.5px] border-t" style={{ borderColor: "var(--rs-border)" }}>
                <div className="truncate font-mono text-[var(--rs-text-1)]">{f}</div>
                <div className="truncate text-[var(--rs-text-1)]">
                  {r.value === "" ? <span className="text-[var(--rs-text-3)]">—</span> : String(r.value)}
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: _color(r.confidence) }} />
                  <span className="text-[10px] label-up">{r.confidence}</span>
                </div>
                <div className="truncate text-[var(--rs-text-2)]">
                  <span className="text-[var(--rs-text-3)] text-[10px] mr-1.5">{classifyField(f)}</span>
                  {r.source?.module ? `${r.source.module}${r.source.label ? " · " + r.source.label : ""}` : "—"}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {overrides.length > 0 && (
        <div
          className="rounded-md border mt-4 p-3 text-[11.5px]"
          style={{ borderColor: "var(--rs-border)", background: "rgba(22,24,29,0.65)" }}
        >
          <div className="label-up text-[9.5px] text-[var(--rs-text-3)] mb-2">
            Active overrides ({overrides.length})
          </div>
          <ul className="space-y-1">
            {overrides.map((o) => (
              <li key={o.id} className="flex gap-2">
                <span className="text-[var(--rs-text-3)]">{o.scope}</span>
                <span className="text-[var(--rs-text-2)] font-mono">{o.fieldPath}</span>
                <span className="text-[var(--rs-text-1)]">{String(o.value)}</span>
                {o.reason && <span className="text-[var(--rs-text-3)] italic">— {o.reason}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function HealthTile({ label, value, testid, tone }) {
  return (
    <div
      data-testid={testid}
      className="rounded border px-2 py-1.5"
      style={{ borderColor: "var(--rs-border)", background: tone || "rgba(10,11,14,0.6)" }}
    >
      <div className="text-[9px] label-up text-[var(--rs-text-3)]">{label}</div>
      <div className="text-[13px] font-semibold text-[var(--rs-text-1)]">{value}</div>
    </div>
  );
}

function HealthStrip({ ctx }) {
  const h = summariseHealth(ctx);
  return (
    <div
      className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3"
      data-testid="ctx-health-strip"
    >
      <HealthTile label="Coverage"  value={`${h.coveragePct}%`}     testid="ctx-health-coverage" tone="rgba(34,197,94,0.08)" />
      <HealthTile label="Inherited" value={h.byConfidence.inherited} testid="ctx-health-inherited" />
      <HealthTile label="Derived"   value={h.byConfidence.derived}   testid="ctx-health-derived" />
      <HealthTile label="Exact"     value={h.byConfidence.exact}     testid="ctx-health-exact" />
      <HealthTile label="Conflicts" value={h.byConfidence.conflict}  testid="ctx-health-conflicts" tone={h.byConfidence.conflict > 0 ? "rgba(239,68,68,0.10)" : ""} />
      <HealthTile label="Unknown"   value={h.byConfidence.unknown}   testid="ctx-health-unknown" tone="rgba(107,114,128,0.10)" />
    </div>
  );
}
